// Elegibilidad por nivel del catálogo con lo que ya trae `GET /portal/catalog`: los `levels`
// de cada fila y los `levelPlans` de la primera página. Puro.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { TIER_LABEL } from '@/shared/patterns/Pills/tierPresentation';
import type {
    PortalCatalogPage,
    PortalCatalogRow,
    PortalLevelPlan,
} from '@/api/services/retail-wizard/contract';
import type { PlanLevel } from '@/domains/sales/wizard/types';
import {
    EMPTY_FILTERS,
    filterCatalog,
    type CatalogExplorerFilters,
    type StatFilter,
} from './catalogExplorer';
import { acceptsSurekeep, shortThousands } from '../../phones/lib/levelPlans';

/** «Oro», «Plata» o «Bronce», de la fuente única de los niveles. */
export function levelLabel(level: PlanLevel): string {
    return TIER_LABEL[ClientLevelEnum[level]];
}

export const PLANS_UNREADABLE = 'No se pudieron leer los planes de crédito de tu tienda.';
export const PLANS_EMPTY = 'Tu tienda no tiene planes de crédito por nivel.';

export interface CatalogFilters extends CatalogExplorerFilters {
    /** Nivel cuyo plan tiene que alcanzar al equipo; `''` es «Todos». */
    level: PlanLevel | '';
}

export const EMPTY_CATALOG_FILTERS: CatalogFilters = { ...EMPTY_FILTERS, level: '' };

export type LevelEligibility =
    | { kind: 'eligible'; minDownPaymentPct: number }
    | { kind: 'notFinanceable' }
    | { kind: 'cap'; maxFinancedCents: number }
    | { kind: 'unknown' };

/** Si el plan del nivel alcanza al equipo y con qué enganche mínimo, según la fila del BFF. */
export function levelEligibility(row: PortalCatalogRow, plan: PortalLevelPlan): LevelEligibility {
    if (!acceptsSurekeep(row) || row.financeable === false) return { kind: 'notFinanceable' };
    const fit = row.levels?.find((candidate) => candidate.level === plan.level);
    if (fit === undefined) return { kind: 'unknown' };
    if (!fit.eligible) return { kind: 'cap', maxFinancedCents: plan.maxFinancedAmountCents };
    const price = row.cashPriceCents;
    return {
        kind: 'eligible',
        minDownPaymentPct:
            fit.minDownPaymentCents !== undefined && price > 0
                ? fit.minDownPaymentCents / price
                : plan.minDownPaymentPct,
    };
}

/** Filtro de nivel: sin nivel pasa todo; con nivel, solo lo que la fila marca elegible. */
export function isEligibleForLevel(row: PortalCatalogRow, level: PlanLevel | ''): boolean {
    if (level === '') return true;
    return row.levels?.some((fit) => fit.level === level && fit.eligible) ?? false;
}

/** «Máx: Oro $10K · Plata $8K · Bronce $5K» con el tope financiable de cada plan. */
export function levelCapsNote(plans: PortalLevelPlan[]): string | null {
    if (plans.length === 0) return null;
    const caps = plans.map(
        (plan) => `${levelLabel(plan.level)} ${shortThousands(plan.maxFinancedAmountCents)}`,
    );
    return `Máx: ${caps.join(' · ')}`;
}

export interface LevelPlansState {
    /** `null` mientras no llega la primera página o si los planes no se pudieron leer. */
    plans: PortalLevelPlan[] | null;
    /** Los topes por nivel, o por qué no hay planes. */
    note: string | null;
}

/** Los planes por nivel salen de la primera página, que es la única que los trae. */
export function levelPlansOf(
    firstPage: Pick<PortalCatalogPage, 'items' | 'levelPlans'> | undefined,
): LevelPlansState {
    if (firstPage === undefined) return { plans: null, note: null };
    const plans = firstPage.levelPlans ?? null;
    if (plans === null) return { plans: null, note: PLANS_UNREADABLE };
    return { plans, note: levelCapsNote(plans) ?? PLANS_EMPTY };
}

/** Filtros, píldora y nivel; sin planes legibles el nivel se ignora en vez de vaciar la tabla. */
export function filterCatalogByLevel(
    rows: PortalCatalogRow[],
    filters: CatalogFilters,
    stat: StatFilter,
    plans: PortalLevelPlan[] | null,
): PortalCatalogRow[] {
    const visible = filterCatalog(rows, filters, stat);
    if (plans === null || filters.level === '') return visible;
    return visible.filter((row) => isEligibleForLevel(row, filters.level));
}
